import { useEffect, useState } from "react";
import {
	fetchDeviceSharedVolumes,
	listEnvironmentSharedVolumes,
	scanDeviceSharedVolumes,
	updateEnvironmentVolumes,
	type DeviceSharedVolumeDevice,
	type Environment,
	type SharedVolume,
} from "@/lib/api";
import { summarizeScan } from "@/lib/device-shared-volumes";
import { SHARED_VOLUME_STATE_LABELS, isSharedVolumeMountable } from "@/lib/shared-volumes";
import { refreshCloudSharedVolumes } from "@/stores/useCloudSharedVolumeStore";
import { refreshDeviceSharedVolumes } from "@/stores/useDeviceSharedVolumeStore";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Icon } from "@/components/common/Icon";

interface EnvironmentVolumesPanelProps {
	/** 設定対象の環境。 */
	environment: Environment;
	/** 環境 Pod が存在するか。クラウドの環境では、ある間はマウント解除ができない (サーバー側も拒否する)。 */
	podExists: boolean;
	/** 保存成功時に更新後の環境を通知する。 */
	onSaved: (updated: Environment) => void;
}

const DETACH_BLOCKED_REASON =
	"起動中のエージェントからは外せません。エージェントを停止してから外してください。";

const DEVICE_UNKNOWN_REASON = "デバイスの様子を取得できませんでした。";

const DETACH_DISCARDS_CHANGES =
	"外すと、手元の写しからこの変更も消えます。残すには、起動してからファイルのツリーでこの共有ボリュームの行を右クリックして反映してください。";

function errorMessage(err: unknown): string {
	return err instanceof Error ? err.message : "予期しないエラーが発生しました。";
}

/**
 * 環境にマウントする共有ボリュームの選択フォーム。設定ダイアログの「共有ボリューム」タブに埋め込まれる。
 * 環境の組織の一覧から選び、保存で宣言を全て置き換える。クラウドの環境は次回起動時に反映する。
 * デバイスの環境は再起動なしで付け外しでき、外すときは手元の写しを消すので、反映していない変更があれば確かめる。
 */
export function EnvironmentVolumesPanel({
	environment,
	podExists,
	onSaved,
}: EnvironmentVolumesPanelProps) {
	const onDevice = environment.runner !== null;
	const [volumeIds, setVolumeIds] = useState<string[]>(environment.volumeIds);
	const [volumes, setVolumes] = useState<SharedVolume[] | null>(null);
	const [volumesError, setVolumesError] = useState<string | null>(null);
	const [device, setDevice] = useState<DeviceSharedVolumeDevice | null>(null);
	const [deviceFailed, setDeviceFailed] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);
	/** 付けたことをデバイスに知らせられなかったときの注意。 */
	const [syncNotice, setSyncNotice] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	/** 外す前に確かめる文言。null なら確かめていない。 */
	const [detachConfirm, setDetachConfirm] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		setVolumesError(null);
		const load = async () => {
			try {
				const data = await listEnvironmentSharedVolumes(environment.id);
				if (!cancelled) setVolumes(data);
			} catch (err) {
				if (!cancelled) setVolumesError(errorMessage(err));
				throw err;
			}
		};
		void load().catch((err: unknown) => {
			console.error("共有ボリュームの一覧を取得できませんでした", err);
		});
		return () => {
			cancelled = true;
		};
	}, [environment.id]);

	// 外せない・付けられない理由 (オフライン・ランナーが古い) を出し、登録を解除したデバイスなら送らずに外せるようにするため、
	// デバイスの様子を取る。
	useEffect(() => {
		if (!onDevice) return;
		let cancelled = false;
		setDeviceFailed(false);
		const load = async () => {
			try {
				const data = await fetchDeviceSharedVolumes(environment.id);
				if (!cancelled) setDevice(data.device);
			} catch (err) {
				if (!cancelled) {
					setVolumesError(errorMessage(err));
					setDeviceFailed(true);
				}
				throw err;
			}
		};
		void load().catch((err: unknown) => {
			console.error("デバイスの様子を取得できませんでした", err);
		});
		return () => {
			cancelled = true;
		};
	}, [environment.id, onDevice]);

	async function save() {
		const updated = await updateEnvironmentVolumes(environment.id, volumeIds);
		onSaved(updated);
		setSaved(true);
		const sync = updated.sharedVolumeSync;
		setSyncNotice(
			sync && !sync.sent
				? `デバイスに知らせられませんでした。${sync.reason ?? ""}次の起動で取り込みます。`
				: null,
		);
		if (onDevice) refreshDeviceSharedVolumes(environment.id);
		else refreshCloudSharedVolumes(environment.id);
	}

	/**
	 * 外すボリュームの反映していない手元の変更を数え直し、消える変更があるか数えられなければ確かめる文言を返す (無ければ
	 * null)。登録を解除したデバイスは写しに届かない (消しも反映もできない) ので数えない。
	 */
	async function detachConfirmMessage(): Promise<string | null> {
		const detached = environment.volumeIds.filter((id) => !volumeIds.includes(id));
		if (!onDevice || detached.length === 0 || device?.revoked) return null;
		const scanned = await scanDeviceSharedVolumes(environment.id);
		const { unpushed, uncountedMessage } = summarizeScan({
			...scanned,
			volumes: scanned.volumes.filter((volume) => detached.includes(volume.volumeId)),
		});
		if (unpushed === 0 && uncountedMessage === null) return null;
		return [
			uncountedMessage ?? "",
			unpushed > 0 ? `反映していない変更が ${unpushed} 件あります。` : "",
			DETACH_DISCARDS_CHANGES,
		].join("");
	}

	// 数え直せなくても外せるよう、理由を添えて確認を開く。
	async function submit() {
		let confirm: string | null;
		try {
			confirm = await detachConfirmMessage();
		} catch (err) {
			setDetachConfirm(
				`共有ボリュームに反映していない変更があるかを確かめられませんでした (${errorMessage(err)})。外すと、反映していない変更があっても手元の写しから消えます。`,
			);
			throw err;
		}
		if (confirm !== null) {
			setDetachConfirm(confirm);
			return;
		}
		try {
			await save();
		} catch (err) {
			setSubmitError(errorMessage(err));
			throw err;
		}
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setSubmitError(null);
		setSaved(false);
		setSyncNotice(null);
		setLoading(true);
		try {
			await submit();
		} finally {
			setLoading(false);
		}
	}

	// デバイスの環境で保存済みのボリュームを外せない理由。登録を解除したデバイスは送らずに外せる。
	const deviceDetachReason =
		device === null
			? deviceFailed
				? DEVICE_UNKNOWN_REASON
				: null
			: device.revoked
				? null
				: device.unavailableReason;

	return (
		<form
			onSubmit={(e) => {
				handleSubmit(e).catch((err: unknown) => {
					console.error("共有ボリュームの付け外しを保存できませんでした", err);
				});
			}}
			noValidate
			data-testid="environment-volumes-panel"
			style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
		>
			<div
				style={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					padding: "20px 24px",
					display: "flex",
					flexDirection: "column",
					gap: 16,
				}}
			>
				<span
					style={{
						fontSize: "var(--text-caption)",
						color: "var(--fg-2)",
						fontFamily: "var(--font-sans)",
					}}
				>
					{onDevice
						? "組織の共有ボリュームをこのエージェントで使えるようにします。取り込むファイルをチャットのファイルのツリー (~ の上に共有ボリュームごとのフォルダが出ます) で選ぶと、デバイスに写して /mnt/volumes/<名前> から読み書きできます。"
						: "組織の共有ボリュームをこのエージェントにマウントします。マウント先は /mnt/volumes/<名前> です。"}
				</span>
				{volumesError && (
					<span
						style={{
							fontSize: "var(--text-caption)",
							color: "var(--danger)",
							fontFamily: "var(--font-sans)",
						}}
					>
						{volumesError}
					</span>
				)}
				{volumes && volumes.length === 0 && (
					<span
						data-testid="environment-volumes-empty"
						style={{
							fontSize: "var(--text-caption)",
							color: "var(--fg-3)",
							fontFamily: "var(--font-sans)",
						}}
					>
						共有ボリュームはありません。サイドバーの「共有ボリューム」から作成できます。
					</span>
				)}
				{volumes && volumes.length > 0 && (
					<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
						{volumes.map((volume) => {
							const checked = volumeIds.includes(volume.id);
							// クラウドの環境の保存済みの宣言 (= Pod がマウントしている可能性があるもの) は Pod が無い間だけ外せる。
							// デバイスの環境は、写しを消す命令を送れるとき (オンラインで版が足りる) と、登録を解除したデバイス (送らずに
							// 外す) のときだけ外せ、版が足りなければ付けられない。
							// まだ保存していないチェックは自由に付け外しできる。
							const declared = environment.volumeIds.includes(volume.id);
							const disabledReason =
								checked && declared
									? onDevice
										? deviceDetachReason
										: podExists
											? DETACH_BLOCKED_REASON
											: null
									: !checked && !isSharedVolumeMountable(volume)
										? `${SHARED_VOLUME_STATE_LABELS[volume.state]}のため${onDevice ? "付けられません" : "マウントできません"}`
										: !checked && !declared && device !== null && !device.runnerSupported
											? device.unavailableReason
											: null;
							const selectable = disabledReason === null;
							return (
								<label
									key={volume.id}
									data-tooltip={disabledReason ?? undefined}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
										fontSize: "var(--text-ui)",
										color: "var(--fg-2)",
										fontFamily: "var(--font-sans)",
										cursor: selectable ? "pointer" : "not-allowed",
										opacity: selectable ? 1 : 0.5,
									}}
								>
									<input
										type="checkbox"
										checked={checked}
										disabled={!selectable}
										onChange={(e) =>
											setVolumeIds((prev) =>
												e.target.checked
													? [...prev, volume.id]
													: prev.filter((id) => id !== volume.id),
											)
										}
										data-testid={`environment-volume-${volume.id}`}
									/>
									<span>{volume.name}</span>
									<span
										style={{
											fontSize: "var(--text-caption)",
											color: "var(--fg-3)",
											fontFamily: "var(--font-mono)",
										}}
									>
										{volume.mountPath}
									</span>
								</label>
							);
						})}
					</div>
				)}
				{submitError && (
					<div
						style={{
							padding: "10px 14px",
							borderRadius: "var(--radius-3)",
							background: "var(--danger-subtle)",
							boxShadow: "0 0 0 1px var(--danger-line)",
							fontSize: "var(--text-ui)",
							color: "var(--danger)",
							fontFamily: "var(--font-sans)",
						}}
					>
						{submitError}
					</div>
				)}
				{syncNotice && (
					<div
						data-testid="environment-volumes-sync-notice"
						style={{
							display: "flex",
							alignItems: "flex-start",
							gap: 6,
							fontSize: "var(--text-caption)",
							lineHeight: "var(--leading-normal)",
							color: "var(--fg-2)",
							fontFamily: "var(--font-sans)",
						}}
					>
						<span
							style={{
								flex: "none",
								display: "inline-flex",
								alignItems: "center",
								height: "1.5em",
							}}
						>
							<Icon name="info" size={13} />
						</span>
						<span>{syncNotice}</span>
					</div>
				)}
				{!onDevice && (
					<div
						style={{
							display: "flex",
							alignItems: "flex-start",
							gap: 6,
							paddingTop: 4,
							fontSize: "var(--text-caption)",
							lineHeight: "var(--leading-normal)",
							color: "var(--fg-2)",
							fontFamily: "var(--font-sans)",
						}}
					>
						<span
							style={{
								flex: "none",
								display: "inline-flex",
								alignItems: "center",
								height: "1.5em",
							}}
						>
							<Icon name="info" size={13} />
						</span>
						<span>マウントの変更は、このエージェントの次回起動時から反映されます。</span>
					</div>
				)}
			</div>
			<div
				style={{
					flex: "none",
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					gap: 12,
					borderTop: "var(--pane-header-border)",
					padding: "12px 24px",
				}}
			>
				{saved && (
					<span
						data-testid="environment-volumes-saved"
						style={{
							fontSize: "var(--text-caption)",
							color: "var(--fg-2)",
							fontFamily: "var(--font-sans)",
						}}
					>
						保存しました。
					</span>
				)}
				<ConfirmButton type="submit" label="保存" loading={loading} />
			</div>
			<ConfirmDialog
				open={detachConfirm !== null}
				title="共有ボリュームを外す"
				message={detachConfirm ?? ""}
				confirmLabel="外す"
				onConfirm={async () => {
					await save();
					setDetachConfirm(null);
				}}
				onCancel={() => setDetachConfirm(null)}
			/>
		</form>
	);
}
